/**
 * Scheduled Jobs — a top-level admin tab to schedule unattended jobs.
 *
 * Three job kinds (backup → .sql artifact, sql-script on a connection or
 * [connection] fleet, webhook POST), a structured schedule picker (no cron dep:
 * interval / daily / weekly / monthly), an optional notify hook to a vaulted
 * notification target, per-statement "run now", and run history with artifact
 * download. The scheduler goroutine lives server-side (internal/jobs); this panel
 * is the management surface.
 */
import { createSignal, createResource, createMemo, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import Modal from '../components/Modal.jsx';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { listTargets } from '../lib/notificationtargets.js';
import {
  listJobs, createJob, updateJob, deleteJob, runJobNow, listJobRuns, listRecentRuns, artifactUrl,
} from '../lib/jobs.js';

const fmtDur = (ms) => ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtTime = (unix) => (unix ? new Date(unix * 1000).toLocaleString() : '—');
const pad2 = (n) => String(n).padStart(2, '0');
const atMinToTime = (m) => `${pad2(Math.floor((m || 0) / 60))}:${pad2((m || 0) % 60)}`;
const timeToAtMin = (t) => {
  const [h, m] = (t || '00:00').split(':').map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};

// Per-kind default config when creating a job or switching kind.
const defaultConfig = (kind) => {
  if (kind === 'backup') return {
    targets: [], dataMode: 'full',
    // Include/format toggles, mirroring the manual Backup dialog. Everything on
    // by default for a complete, restorable backup; gzip on (backups are stored);
    // DEFINER kept (faithful — flip on to restore under the importing account).
    triggers: true, routines: true, views: true, events: true,
    singleTransaction: true, gzip: true, stripDefiner: false,
    // Per-table backup tags are OFF by default — a scheduled backup is a full
    // backup unless the admin opts in here.
    honorStructureOnly: false,
    // Correctness manifest (structure + per-table counts + db collation) and the
    // optional data CRC both OFF by default — opt in for verifiable backups.
    correctness: false, checksum: false,
  };
  if (kind === 'sql-script') return { connectionId: 0, sql: '', onError: 'stop' };
  return { targetId: 0, title: 'Ninocat job', message: '' }; // webhook
};

// Default schedule UI state (everyN/everyUnit/time carry interval + clock fields;
// converted to a server Spec on save). Shared by a new job and edit-inflation.
const defaultScheduleUI = () => ({ kind: 'daily', everyN: 1, everyUnit: 'hour', time: '02:00', days: [1, 2, 3, 4, 5], dom: 1, windowed: false, winStart: '08:00', winEnd: '17:00' });

const blankInput = () => ({
  name: '', kind: 'backup', enabled: true,
  config: defaultConfig('backup'),
  schedule: defaultScheduleUI(),
  notifySuccessTargetId: 0, notifyFailureTargetId: 0,
});

// Build the server schedule.Spec from the UI schedule state. everyMin / dom are
// Go ints server-side — a decimal (1.5 min, 0.1 h = 6.000…01) fails the whole
// request, so they're rounded to whole numbers.
const toSpec = (s) => {
  if (s.kind === 'interval') {
    const spec = { kind: 'interval', everyMin: Math.max(1, Math.round((Number(s.everyN) || 1) * (s.everyUnit === 'hour' ? 60 : 1))) };
    if (s.windowed) { spec.windowed = true; spec.windowStartMin = timeToAtMin(s.winStart); spec.windowEndMin = timeToAtMin(s.winEnd); }
    return spec;
  }
  if (s.kind === 'daily') return { kind: 'daily', atMin: timeToAtMin(s.time) };
  if (s.kind === 'weekly') return { kind: 'weekly', atMin: timeToAtMin(s.time), days: [...s.days].sort((a, b) => a - b) };
  return { kind: 'monthly', atMin: timeToAtMin(s.time), dom: Math.round(Number(s.dom)) || 1 };
};

// Inflate a stored Spec back into UI schedule state (for editing).
const fromSpec = (spec) => {
  const base = { ...defaultScheduleUI(), kind: spec.kind || 'daily' };
  if (spec.kind === 'interval') {
    const m = spec.everyMin || 60;
    if (m % 60 === 0) { base.everyN = m / 60; base.everyUnit = 'hour'; }
    else { base.everyN = m; base.everyUnit = 'min'; }
    if (spec.windowed) { base.windowed = true; base.winStart = atMinToTime(spec.windowStartMin || 0); base.winEnd = atMinToTime(spec.windowEndMin || 0); }
  } else {
    base.time = atMinToTime(spec.atMin || 0);
    if (spec.kind === 'weekly') base.days = spec.days && spec.days.length ? spec.days : [1];
    if (spec.kind === 'monthly') base.dom = spec.dom || 1;
  }
  return base;
};

export default function ScheduledJobsPanel(props) {
  const [jobs, { refetch }] = createResource(listJobs);
  const [conns] = createResource(async () => {
    try { return (await api.get('/api/connections')).connections || []; } catch { return []; }
  });
  const [targets] = createResource(listTargets);
  const [busyId, setBusyId] = createSignal(0);
  const [histJob, setHistJob] = createSignal(null);

  // Two sub-tabs: the schedule list, and a global run-history feed across all
  // jobs. The history resource only fetches once the tab is opened (the source
  // is undefined on the Schedules tab, so createResource skips the fetch), and
  // re-fetches on each re-entry.
  const [tab, setTab] = createSignal('schedules');
  const [recentRuns, { refetch: refetchHistory }] =
    createResource(() => tab() === 'history' || undefined, () => listRecentRuns(200));

  const [ed, setEd] = createStore({ open: false, isNew: true, id: 0, input: blankInput(), busy: false });

  // Per-connection database lists (lazy, cached) for the backup target picker.
  // Listing dials the live server, so an unreachable connection surfaces an error
  // per target rather than a silently-empty list.
  const [dbCache, setDbCache] = createSignal({}); // connId -> { state:'loading'|'ok'|'error', list, error }
  const loadDbsFor = async (idRaw) => {
    const id = Number(idRaw) || 0;
    if (!id) return;
    const cur = dbCache()[id];
    if (cur && cur.state !== 'error') return; // already loaded or loading
    setDbCache((p) => ({ ...p, [id]: { state: 'loading', list: [] } }));
    try {
      const list = (await api.get(`/api/connections/${id}/databases`)).databases || [];
      setDbCache((p) => ({ ...p, [id]: { state: 'ok', list } }));
    } catch (e) {
      setDbCache((p) => ({ ...p, [id]: { state: 'error', list: [], error: e?.message || String(e) } }));
    }
  };

  const openNew = () => setEd({ open: true, isNew: true, id: 0, input: blankInput(), busy: false });

  // Inflate a stored job into editor input (folds legacy backup fields into the
  // multi-connection targets shape + loads its databases). Shared by Edit + Duplicate.
  const editorInputFromJob = (j) => {
    const config = { ...defaultConfig(j.kind), ...(j.config || {}) };
    if (j.kind === 'backup') {
      if (!Array.isArray(config.targets) || config.targets.length === 0) {
        const legacy = (config.databases && config.databases.length)
          ? config.databases
          : (config.database ? [config.database] : []);
        config.targets = (config.connectionId && legacy.length)
          ? [{ connectionId: config.connectionId, databases: legacy }]
          : [];
      }
      for (const t of config.targets) loadDbsFor(t.connectionId);
    }
    return {
      name: j.name, kind: j.kind, enabled: j.enabled,
      config,
      schedule: fromSpec(j.schedule || {}),
      notifySuccessTargetId: j.notifySuccessTargetId || 0, notifyFailureTargetId: j.notifyFailureTargetId || 0,
    };
  };

  const openEdit = (j) => setEd({ open: true, isNew: false, id: j.id, busy: false, input: editorInputFromJob(j) });

  // Duplicate: open the editor as a NEW job pre-filled from this one, with a
  // "(copy)" name (the name is UNIQUE) — the admin reviews/renames, then Creates.
  const duplicate = (j) => {
    const input = editorInputFromJob(j);
    input.name = `${j.name} (copy)`;
    setEd({ open: true, isNew: true, id: 0, busy: false, input });
  };

  const closeEd = () => setEd('open', false);

  const setKind = (kind) => { setEd('input', 'kind', kind); setEd('input', 'config', defaultConfig(kind)); };

  const toggleDay = (d) => {
    const cur = ed.input.schedule.days;
    setEd('input', 'schedule', 'days', cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);
  };

  // Backup multi-connection target helpers.
  const bkTargets = () => ed.input.config.targets || [];
  const setTargets = (next) => setEd('input', 'config', 'targets', next);
  const connName = (id) => (conns() || []).find((c) => c.id === Number(id))?.name || `conn ${id}`;
  const availableConns = () => (conns() || []).filter((c) => !bkTargets().some((t) => t.connectionId === c.id));
  const dbListFor = (connId) => dbCache()[connId]?.list || [];
  const targetDbList = (connId) => bkTargets().find((t) => t.connectionId === connId)?.databases || [];
  const totalSelectedDbs = () => bkTargets().reduce((n, t) => n + (t.databases || []).length, 0);
  const addBackupTarget = (connIdRaw) => {
    const id = Number(connIdRaw) || 0;
    if (!id || bkTargets().some((t) => t.connectionId === id)) return;
    setTargets([...bkTargets(), { connectionId: id, databases: [] }]);
    loadDbsFor(id);
  };
  const removeBackupTarget = (id) => setTargets(bkTargets().filter((t) => t.connectionId !== id));
  const toggleTargetDb = (connId, name) => setTargets(bkTargets().map((t) => {
    if (t.connectionId !== connId) return t;
    const has = (t.databases || []).includes(name);
    return { ...t, databases: has ? t.databases.filter((d) => d !== name) : [...(t.databases || []), name] };
  }));
  const allTargetDbsSelected = (connId) => {
    const all = dbListFor(connId).map((d) => d.name);
    const sel = targetDbList(connId);
    return all.length > 0 && all.every((n) => sel.includes(n));
  };
  const toggleAllTargetDbs = (connId) => {
    const all = dbListFor(connId).map((d) => d.name);
    const allSel = allTargetDbsSelected(connId);
    setTargets(bkTargets().map((t) => t.connectionId === connId ? { ...t, databases: allSel ? [] : all } : t));
  };

  const buildPayload = () => {
    const i = ed.input;
    const cfg = { ...i.config };
    if (i.kind === 'backup') {
      cfg.targets = (cfg.targets || [])
        .map((t) => ({ connectionId: Number(t.connectionId) || 0, databases: (t.databases || []).filter(Boolean) }))
        .filter((t) => t.connectionId && t.databases.length);
      delete cfg.connectionId; // legacy single-connection fields; superseded by targets[]
      delete cfg.database;
      delete cfg.databases;
    }
    if (i.kind === 'sql-script') cfg.connectionId = Number(cfg.connectionId) || 0;
    if (i.kind === 'webhook') cfg.targetId = Number(cfg.targetId) || 0;
    return {
      name: i.name.trim(), kind: i.kind, enabled: i.enabled,
      config: cfg, schedule: toSpec(i.schedule),
      notifySuccessTargetId: Number(i.notifySuccessTargetId) || 0,
      notifyFailureTargetId: Number(i.notifyFailureTargetId) || 0,
    };
  };

  const save = async () => {
    setEd('busy', true);
    try {
      const payload = buildPayload();
      if (ed.isNew) await createJob(payload);
      else await updateJob(ed.id, payload);
      toast.success('Job saved.');
      closeEd();
      refetch();
    } catch (err) { toast.error('Save failed: ' + err.message); }
    finally { setEd('busy', false); }
  };

  const inputFromJob = (j) => ({
    name: j.name, kind: j.kind, enabled: j.enabled, config: j.config,
    schedule: j.schedule,
    notifySuccessTargetId: j.notifySuccessTargetId, notifyFailureTargetId: j.notifyFailureTargetId,
  });

  const toggleEnabled = async (j) => {
    setBusyId(j.id);
    try { await updateJob(j.id, { ...inputFromJob(j), enabled: !j.enabled }); refetch(); }
    catch (err) { toast.error('Update failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const runNow = async (j) => {
    setBusyId(j.id);
    try {
      const r = await runJobNow(j.id);
      if (r.started) toast.success(`"${j.name}" started — check History in a moment.`);
      else toast.info(`"${j.name}" is already running.`);
      setTimeout(refetch, 1500);
    } catch (err) { toast.error('Run failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const remove = async (j) => {
    if (!confirm(`Delete scheduled job "${j.name}"? Its run history is removed too.`)) return;
    setBusyId(j.id);
    try { await deleteJob(j.id); toast.success('Job deleted.'); refetch(); }
    catch (err) { toast.error('Delete failed: ' + err.message); }
    finally { setBusyId(0); }
  };

  const kindLabel = (k) => k === 'sql-script' ? 'SQL script' : k === 'webhook' ? 'Webhook' : 'Backup';

  return (
    <div class="jobs-panel">
      <div class="jobs-head">
        <div>
          <h2 style="margin:0;">Scheduled Jobs</h2>
          <p class="muted" style="font-size:12px;margin:4px 0 0;">
            Unattended backups, SQL scripts, and webhooks on a schedule. Admin only — jobs run with stored connection credentials and are audited.
          </p>
        </div>
        <Show when={tab() === 'schedules'}>
          <button class="btn" onClick={openNew}>+ New job</button>
        </Show>
      </div>

      {/* Schedules ⇄ Run History sub-tabs */}
      <div class="view-toggle" role="group" aria-label="Scheduled Jobs view" style="margin-bottom:14px;">
        <button class={`view-toggle-btn ${tab() === 'schedules' ? 'active' : ''}`} style="font-size:13px;"
                onClick={() => setTab('schedules')}>Schedules</button>
        <button class={`view-toggle-btn ${tab() === 'history' ? 'active' : ''}`} style="font-size:13px;"
                onClick={() => setTab('history')}>Run History</button>
      </div>

      <Show when={tab() === 'schedules'}>
      <Show when={!jobs.loading} fallback={<div class="muted" style="padding:20px;">Loading…</div>}>
        <Show when={(jobs() || []).length === 0}>
          <div class="muted" style="padding:20px;font-size:13px;">No scheduled jobs yet. Create one to automate a backup, a maintenance script, or a webhook.</div>
        </Show>
        <Show when={(jobs() || []).length > 0}>
          <table class="jobs-table">
            <thead><tr>
              <th>Name</th><th>Kind</th><th>Schedule</th><th>Next run</th><th>Last run</th><th>On</th><th></th>
            </tr></thead>
            <tbody>
              <For each={jobs() || []}>
                {(j) => (
                  <tr classList={{ 'job-disabled': !j.enabled }}>
                    <td class="job-name">{j.name}<Show when={!j.enabled}><span class="job-paused-badge" title="This schedule is paused — it won't run until resumed">Paused</span></Show></td>
                    <td>{kindLabel(j.kind)}</td>
                    <td class="muted">{j.scheduleText}</td>
                    <td class="muted">{j.enabled ? fmtTime(j.nextRunAt) : <span class="muted">paused</span>}</td>
                    <td>
                      <Show when={j.latestRun} fallback={<span class="muted">never</span>}>
                        <span class={'run-badge run-' + j.latestRun.status} title={j.latestRun.error || j.latestRun.summary}>
                          {j.latestRun.status}
                        </span>
                        <span class="muted" style="margin-left:6px;">{fmtTime(j.latestRun.startedAt)}</span>
                      </Show>
                    </td>
                    <td>
                      <label class="job-toggle" title={j.enabled ? 'Enabled' : 'Disabled'}>
                        <input type="checkbox" checked={j.enabled} disabled={busyId() === j.id} onChange={() => toggleEnabled(j)} />
                      </label>
                    </td>
                    <td style="text-align:right;white-space:nowrap;">
                      <button class="btn btn-sm" classList={{ 'btn-ghost': j.enabled }} disabled={busyId() === j.id}
                              onClick={() => toggleEnabled(j)}
                              title={j.enabled ? 'Pause this schedule — it stops running until resumed' : 'Resume this schedule'}>
                        {j.enabled ? '⏸ Pause' : '▶ Resume'}
                      </button>
                      <button class="btn btn-sm" disabled={busyId() === j.id} onClick={() => runNow(j)}>Run now</button>
                      <button class="btn btn-sm btn-ghost" onClick={() => setHistJob(j)}>History</button>
                      <button class="btn btn-sm btn-ghost" onClick={() => openEdit(j)}>Edit</button>
                      <button class="btn btn-sm btn-ghost" onClick={() => duplicate(j)} title="Create a copy of this job">⧉ Duplicate</button>
                      <button class="btn btn-sm btn-danger" disabled={busyId() === j.id} onClick={() => remove(j)}>Delete</button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
      </Show>

      {/* ── Run History (global, across all jobs) ── */}
      <Show when={tab() === 'history'}>
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <span class="muted" style="font-size:12px;">Most recent {(recentRuns() || []).length} runs across all jobs.</span>
          <button class="btn btn-sm btn-ghost" onClick={refetchHistory}>Refresh</button>
        </div>
        <Show when={!recentRuns.loading} fallback={<div class="muted" style="padding:20px;">Loading…</div>}>
          <Show when={(recentRuns() || []).length === 0}>
            <div class="muted" style="padding:20px;font-size:13px;">No runs yet. Jobs appear here after they run (scheduled or via "Run now").</div>
          </Show>
          <Show when={(recentRuns() || []).length > 0}>
            <table class="jobs-table">
              <thead><tr>
                <th>Started</th><th>Job</th><th>Kind</th><th>Status</th><th>Duration</th><th>Summary</th><th></th>
              </tr></thead>
              <tbody>
                <For each={recentRuns() || []}>
                  {(r) => (
                    <tr>
                      <td class="muted" style="white-space:nowrap;">{fmtTime(r.startedAt)}</td>
                      <td class="job-name">{r.jobName}</td>
                      <td class="muted">{kindLabel(r.jobKind)}</td>
                      <td><span class={'run-badge run-' + r.status}>{r.status}</span></td>
                      <td class="muted">{r.finishedAt ? fmtDur(r.durationMs) : '…'}</td>
                      <td style="font-size:12px;">{r.error ? <span class="run-err-text">{r.error}</span> : r.summary}</td>
                      <td style="text-align:right;">
                        <Show when={r.artifact}><a class="btn btn-sm btn-ghost" href={artifactUrl(r.id)}>Download</a></Show>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </Show>
        </Show>
      </Show>

      {/* ── Editor ── */}
      <Show when={ed.open}>
        <Modal open={true} onClose={closeEd} title={ed.isNew ? 'New scheduled job' : 'Edit job'} wide>
          <div class="settings-row"><label>Name</label>
            <input class="input" value={ed.input.name} onInput={(e) => setEd('input', 'name', e.target.value)} /></div>
          <div class="settings-row"><label>Kind</label>
            <select class="input" value={ed.input.kind} onChange={(e) => setKind(e.target.value)}>
              <option value="backup">Database backup → .sql</option>
              <option value="sql-script">SQL script</option>
              <option value="webhook">Webhook POST</option>
            </select></div>

          {/* Kind-specific config */}
          <Show when={ed.input.kind === 'backup'}>
            <div class="settings-row" style="align-items:flex-start;"><label>Connections</label>
              <div style="flex:1;min-width:0;">
                {/* Each target = one connection + the databases to back up on it. */}
                <For each={bkTargets()}>
                  {(t) => (
                    <div class="jobs-target">
                      <div class="jobs-target-head">
                        <span class="jobs-target-name">{connName(t.connectionId)}</span>
                        <span class="muted" style="font-size:11px;">{(t.databases || []).length} selected</span>
                        <button type="button" class="btn btn-sm btn-ghost" title="Remove this connection"
                                onClick={() => removeBackupTarget(t.connectionId)}>✕</button>
                      </div>
                      <Show when={dbCache()[t.connectionId]?.state === 'loading'}>
                        <span class="muted" style="font-size:12px;">loading databases…</span>
                      </Show>
                      <Show when={dbCache()[t.connectionId]?.state === 'error'}>
                        <span class="muted" style="font-size:12px;color:var(--danger);">
                          Couldn't load databases — {dbCache()[t.connectionId]?.error}. The connection must be reachable.
                        </span>
                      </Show>
                      <Show when={dbCache()[t.connectionId]?.state === 'ok'}>
                        <Show when={dbListFor(t.connectionId).length > 0}
                              fallback={<span class="muted" style="font-size:12px;">No databases visible on this connection.</span>}>
                          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
                            <label class="checkbox" style="font-size:12px;">
                              <input type="checkbox" checked={allTargetDbsSelected(t.connectionId)} onChange={() => toggleAllTargetDbs(t.connectionId)} /><span>Select all</span>
                            </label>
                          </div>
                          <div class="jobs-db-list">
                            <For each={dbListFor(t.connectionId)}>{(d) => (
                              <label class="checkbox jobs-db-item">
                                <input type="checkbox" checked={(t.databases || []).includes(d.name)} onChange={() => toggleTargetDb(t.connectionId, d.name)} />
                                <span class="mono">{d.name}</span>
                              </label>
                            )}</For>
                          </div>
                        </Show>
                      </Show>
                    </div>
                  )}
                </For>
                <Show when={availableConns().length > 0}>
                  <select class="input" style="margin-top:2px;max-width:280px;"
                          onChange={(e) => { addBackupTarget(e.target.value); e.target.value = ''; }}>
                    <option value="">+ Add connection…</option>
                    <For each={availableConns()}>{(c) => <option value={c.id}>{c.name}</option>}</For>
                  </select>
                </Show>
                <Show when={bkTargets().length === 0}>
                  <span class="muted" style="font-size:12px;display:block;margin-top:6px;">Add one or more connections and pick the databases to back up.</span>
                </Show>
                <Show when={totalSelectedDbs() > 1}>
                  <span class="muted" style="font-size:11px;display:block;margin-top:6px;">
                    Backs up {totalSelectedDbs()} databases across {bkTargets().length} connection(s) one after another (sequentially), each its own backup entry.
                  </span>
                </Show>
              </div></div>
            <div class="settings-row"><label>Contents</label>
              <select class="input" value={ed.input.config.dataMode} onChange={(e) => setEd('input', 'config', 'dataMode', e.target.value)}>
                <option value="full">Structure + data</option>
                <option value="structure">Structure only</option>
              </select></div>
            <div class="settings-row"><label>Include</label>
              <div style="display:flex;flex-wrap:wrap;gap:6px 18px;align-items:center;">
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.triggers} onChange={(e) => setEd('input', 'config', 'triggers', e.currentTarget.checked)} /><span>Triggers</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.routines} onChange={(e) => setEd('input', 'config', 'routines', e.currentTarget.checked)} /><span>Routines</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.views} onChange={(e) => setEd('input', 'config', 'views', e.currentTarget.checked)} /><span>Views</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.events} onChange={(e) => setEd('input', 'config', 'events', e.currentTarget.checked)} /><span>Events</span></label>
              </div></div>
            <div class="settings-row"><label>Options</label>
              <div style="display:flex;flex-wrap:wrap;gap:6px 18px;align-items:center;">
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.singleTransaction} onChange={(e) => setEd('input', 'config', 'singleTransaction', e.currentTarget.checked)} /><span title="Single transaction — point-in-time consistent for InnoDB">Consistent snapshot</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.gzip} onChange={(e) => setEd('input', 'config', 'gzip', e.currentTarget.checked)} /><span>Compress (.sql.gz)</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.stripDefiner} onChange={(e) => setEd('input', 'config', 'stripDefiner', e.currentTarget.checked)} /><span title="Restore views/routines/triggers under the importing account">Strip DEFINER</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.honorStructureOnly} onChange={(e) => setEd('input', 'config', 'honorStructureOnly', e.currentTarget.checked)} /><span title="Apply the shared per-table backup tags (structure-only / sample / skip). Off = full backup of every table.">Honor per-table backup tags</span></label>
                <label class="checkbox"><input type="checkbox" checked={ed.input.config.correctness} onChange={(e) => setEd('input', 'config', 'correctness', e.currentTarget.checked)} /><span title="Embed a correctness manifest (structure + per-table row counts + database collation) so a strict restore can verify the result.">Strict (correctness manifest)</span></label>
                <Show when={ed.input.config.correctness}>
                  <label class="checkbox"><input type="checkbox" checked={ed.input.config.checksum} onChange={(e) => setEd('input', 'config', 'checksum', e.currentTarget.checked)} /><span title="Add an order-independent per-table data CRC32 so a strict restore verifies row CONTENTS, not just counts. Scans every table once — slower on big databases.">Verify data (CRC32)</span></label>
                </Show>
              </div></div>
          </Show>

          <Show when={ed.input.kind === 'sql-script'}>
            <div class="settings-row"><label>Default connection</label>
              <select class="input" value={ed.input.config.connectionId} onChange={(e) => setEd('input', 'config', 'connectionId', Number(e.target.value))}>
                <option value="0">— pick (or use [connection] prefixes) —</option>
                <For each={conns() || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
              </select></div>
            <div class="settings-row" style="align-items:flex-start;"><label>SQL</label>
              <textarea class="input mono" rows="6" style="font-size:12px;"
                        placeholder={'-- runs unattended\nOPTIMIZE TABLE big_log;\n[other-conn].DELETE FROM tmp WHERE created < NOW() - INTERVAL 30 DAY;'}
                        value={ed.input.config.sql} onInput={(e) => setEd('input', 'config', 'sql', e.target.value)}></textarea></div>
            <div class="settings-row"><label>On error</label>
              <select class="input" value={ed.input.config.onError} onChange={(e) => setEd('input', 'config', 'onError', e.target.value)}>
                <option value="stop">Stop at first error</option>
                <option value="continue">Continue, record failures</option>
              </select></div>
            <p class="muted" style="font-size:11px;margin:2px 0 0;">Prefix a statement with <code class="mono">[connection name].</code> to route it to another connection (fleet).</p>
          </Show>

          <Show when={ed.input.kind === 'webhook'}>
            <div class="settings-row"><label>Target</label>
              <select class="input" value={ed.input.config.targetId} onChange={(e) => setEd('input', 'config', 'targetId', Number(e.target.value))}>
                <option value="0">— pick —</option>
                <For each={targets() || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
              </select>
              <Show when={(targets() || []).length === 0}><span class="muted" style="font-size:11px;">No targets — create one in Tools → Notification Targets.</span></Show></div>
            <div class="settings-row"><label>Title</label>
              <input class="input" value={ed.input.config.title} onInput={(e) => setEd('input', 'config', 'title', e.target.value)} /></div>
            <div class="settings-row" style="align-items:flex-start;"><label>Message</label>
              <textarea class="input" rows="3" value={ed.input.config.message} onInput={(e) => setEd('input', 'config', 'message', e.target.value)}></textarea></div>
          </Show>

          {/* Schedule picker */}
          <div class="jobs-section-label">Schedule</div>
          <div class="settings-row"><label>Repeat</label>
            <select class="input" value={ed.input.schedule.kind} onChange={(e) => setEd('input', 'schedule', 'kind', e.target.value)}>
              <option value="interval">Every N…</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select></div>

          <Show when={ed.input.schedule.kind === 'interval'}>
            <div class="settings-row"><label>Every</label>
              <div style="display:flex;gap:6px;">
                <input class="input settings-num" type="number" min="1" value={ed.input.schedule.everyN}
                       onInput={(e) => setEd('input', 'schedule', 'everyN', e.target.value)} />
                <select class="input" value={ed.input.schedule.everyUnit} onChange={(e) => setEd('input', 'schedule', 'everyUnit', e.target.value)}>
                  <option value="min">minutes</option>
                  <option value="hour">hours</option>
                </select>
              </div></div>
            <div class="settings-row"><label></label>
              <label class="checkbox" style="font-size:13px;">
                <input type="checkbox" checked={ed.input.schedule.windowed}
                       onChange={(e) => setEd('input', 'schedule', 'windowed', e.currentTarget.checked)} />
                <span>Only within a daily time window</span>
              </label></div>
            <Show when={ed.input.schedule.windowed}>
              <div class="settings-row"><label>Window</label>
                <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
                  <input class="input settings-num" type="time" value={ed.input.schedule.winStart}
                         onInput={(e) => setEd('input', 'schedule', 'winStart', e.target.value)} />
                  <span class="muted">to</span>
                  <input class="input settings-num" type="time" value={ed.input.schedule.winEnd}
                         onInput={(e) => setEd('input', 'schedule', 'winEnd', e.target.value)} />
                  <span class="muted" style="font-size:11px;">server-local — runs only between these times (e.g. skip overnight)</span>
                </div></div>
            </Show>
          </Show>

          <Show when={ed.input.schedule.kind === 'weekly'}>
            <div class="settings-row" style="align-items:flex-start;"><label>On days</label>
              <div style="display:flex;gap:4px;flex-wrap:wrap;">
                <For each={WEEKDAYS}>
                  {(name, i) => (
                    <button type="button" class="day-chip" classList={{ on: ed.input.schedule.days.includes(i()) }}
                            onClick={() => toggleDay(i())}>{name}</button>
                  )}
                </For>
              </div></div>
          </Show>

          <Show when={ed.input.schedule.kind === 'monthly'}>
            <div class="settings-row"><label>Day of month</label>
              <input class="input settings-num" type="number" min="1" max="31" value={ed.input.schedule.dom}
                     onInput={(e) => setEd('input', 'schedule', 'dom', e.target.value)} />
              <span class="muted" style="font-size:11px;">29–31 clamp to the last day of shorter months.</span></div>
          </Show>

          <Show when={ed.input.schedule.kind !== 'interval'}>
            <div class="settings-row"><label>At time</label>
              <input class="input settings-num" type="time" value={ed.input.schedule.time}
                     onInput={(e) => setEd('input', 'schedule', 'time', e.target.value)} />
              <span class="muted" style="font-size:11px;">Server-local time.</span></div>
          </Show>

          {/* Notify hooks — independent targets per outcome. Set both (often the
              same target) to be notified on every run. */}
          <div class="jobs-section-label">Notify (optional)</div>
          <div class="settings-row"><label title="Notification target fired when the run FAILS (error or partial)">On failure → target</label>
            <select class="input" value={ed.input.notifyFailureTargetId} onChange={(e) => setEd('input', 'notifyFailureTargetId', Number(e.target.value))}>
              <option value="0">— none —</option>
              <For each={targets() || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
            </select></div>
          <div class="settings-row"><label title="Notification target fired when the run SUCCEEDS">On success → target</label>
            <select class="input" value={ed.input.notifySuccessTargetId} onChange={(e) => setEd('input', 'notifySuccessTargetId', Number(e.target.value))}>
              <option value="0">— none —</option>
              <For each={targets() || []}>{(t) => <option value={t.id}>{t.name}</option>}</For>
            </select></div>
          <div class="settings-row"><label /><span class="muted" style="font-size:11px;">Set both (e.g. the same target) to be notified on every run.</span></div>

          <div class="settings-row"><label>Enabled</label>
            <input type="checkbox" checked={ed.input.enabled} onChange={(e) => setEd('input', 'enabled', e.target.checked)} /></div>

          <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px;">
            <button class="btn btn-ghost" onClick={closeEd}>Cancel</button>
            <button class="btn" disabled={ed.busy} onClick={save}>{ed.isNew ? 'Create' : 'Save'}</button>
          </div>
        </Modal>
      </Show>

      {/* ── Run history ── */}
      <Show when={histJob()}>
        <RunHistory job={histJob()} onClose={() => setHistJob(null)} />
      </Show>
    </div>
  );
}

function RunHistory(props) {
  const [runs, { refetch }] = createResource(() => props.job.id, listJobRuns);
  return (
    <Modal open={true} onClose={props.onClose} title={`Run history — ${props.job.name}`} wide>
      <div style="display:flex;justify-content:flex-end;margin:0 0 8px;">
        <button class="btn btn-sm btn-ghost" onClick={refetch}>Refresh</button>
      </div>
      <Show when={!runs.loading} fallback={<div class="muted">Loading…</div>}>
        <Show when={(runs() || []).length === 0}><div class="muted" style="font-size:13px;">No runs yet.</div></Show>
        <Show when={(runs() || []).length > 0}>
          <table class="jobs-table">
            <thead><tr><th>Started</th><th>Status</th><th>Duration</th><th>Summary</th><th></th></tr></thead>
            <tbody>
              <For each={runs() || []}>
                {(r) => (
                  <tr>
                    <td class="muted">{fmtTime(r.startedAt)}</td>
                    <td><span class={'run-badge run-' + r.status}>{r.status}</span></td>
                    <td class="muted">{r.finishedAt ? fmtDur(r.durationMs) : '…'}</td>
                    <td style="font-size:12px;">{r.error ? <span class="run-err-text">{r.error}</span> : r.summary}</td>
                    <td style="text-align:right;">
                      <Show when={r.artifact}><a class="btn btn-sm btn-ghost" href={artifactUrl(r.id)}>Download</a></Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </Show>
      </Show>
    </Modal>
  );
}
